import jsPDF from "jspdf";
import "jspdf-autotable";

export const generatePurchaseOrderPdf = async ({ poNum, poData, logoUrl }) => {
  const doc = new jsPDF("p", "mm", "a4", true);

  async function addLogo(url, x, y, width, height) {
    try {
      const response = await fetch(url, { credentials: "include" });

      if (!response.ok) return;

      const blob = await response.blob();
      const img = new Image();
      img.src = URL.createObjectURL(blob);

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      const canvas = document.createElement("canvas");
      const maxWidth = 250;

      canvas.width = maxWidth;
      canvas.height = Math.round((img.height * maxWidth) / img.width);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      const compressed = canvas.toDataURL("image/jpeg", 0.7);
      doc.addImage(compressed, "JPEG", x, y, width, height, "", "FAST");
      URL.revokeObjectURL(img.src);
    } catch (e) {
      console.warn("Failed to load PO logo for PDF:", e);
    }
  }

  let y = 28;

  function checkPageBreak() {
    const pageHeight = doc.internal.pageSize.getHeight();

    if (y + 6 > pageHeight - 20) {
      doc.addPage();
      y = 20;
    }
  }

  function incrementY(value) {
    y += value;
    checkPageBreak();
  }

  function wrapText(text, x, maxWidth) {
    doc.splitTextToSize(text || "", maxWidth).forEach((line) => {
      doc.text(line, x, y);
      incrementY(6);
    });
  }

  const x = logoUrl ? 65 : 14;

  // Header
  doc.setFontSize(16);
  doc.setFont(undefined, "bolditalic");
  doc.text(`Purchase Order #${poNum}`, x, 16);
  doc.setFont(undefined, "normal");
  if (logoUrl) {
    await addLogo(logoUrl, 14, 25, 45, 40);
  }

  // Ship to
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.text("Ship to:", x, y);
  wrapText(poData.shipping?.name, x + 17, 120);
  doc.setFont(undefined, "normal");
  doc.text("Address:", x, y);
  wrapText(poData.shipping?.address, x + 17, 120);
  doc.text("GST:", x, y);
  doc.text(poData.shipping?.taxId || "", x + 17, y);
  incrementY(6);
  doc.text("Phone #:", x, y);
  doc.text(poData.shipping?.phone || "", x + 17, y);
  doc.text("Email:", x + 60, y);
  doc.text(poData.shipping?.email || "", x + 77, y);
  incrementY(6);
  doc.text("Contact:", x, y);
  doc.text(poData.shipping?.person || "", x + 17, y);
  incrementY(10);

  // Bill to
  doc.setFont(undefined, "bold");
  doc.text("Bill to:", x, y);
  wrapText(poData.billing?.name, x + 17, 120);
  doc.setFont(undefined, "normal");
  doc.text("Address:", x, y);
  wrapText(poData.billing?.address, x + 17, 120);
  doc.text("GST:", x, y);
  doc.text(poData.billing?.taxId || "", x + 17, y);
  incrementY(10);

  // Order details
  doc.text("Order Date:", 14, y);
  doc.text(new Date().toLocaleDateString(), 40, y);
  doc.text("Quotation #:", 91, y);
  doc.text(poData.supplier?.quotation || "", 117, y);
  incrementY(6);
  doc.text("PO Author:", 14, y);
  doc.text(poData.user?.name || "", 40, y);
  doc.text("Project:", 91, y);
  wrapText(poData.project || "", 117, 94);

  // Items table
  doc.autoTable({
    startY: y,
    head: [[ "Item Name", "Manufacturer", "Catalog", "# Units", "Unit Price", "Discount", "Tax", "Subtotal" ]],

    body: (poData.items || []).map((item) => [
      item.name || "",
      item.manufacturer || "",
      item.catalog || "",
      item.numUnits || "",
      item.unitPrice || "",
      `${(Number(item.discount || 0) * 100).toFixed(2)}%`,
      `${(Number(item.tax || 0) * 100).toFixed(2)}%`,
      Number(item.subtotal || 0).toFixed(2),
    ]),
    theme: "grid",
  });

  y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : 100;

  // Grand total
  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text(`Grand Total: ${Number(poData.grandTotal || 0).toFixed(2)}`, 141, y);
  doc.setFont(undefined, "normal");
  incrementY(6);

  // Supplier
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.text("Supplier :", 14, y);
  doc.text(poData.supplier?.name || "", 40, y);
  incrementY(6);
  doc.setFont(undefined, "normal");
  doc.text("Address:", 14, y);
  wrapText(poData.supplier?.address || "", 40, 155);
  doc.text("GST:", 14, y);
  doc.text(poData.supplier?.taxId || "", 40, y);
  incrementY(6);
  doc.text("Phone #:", 14, y);
  doc.text(poData.supplier?.phone || "", 40, y);
  doc.text("Email:", 74, y);
  doc.text(poData.supplier?.email || "", 91, y);
  incrementY(6);
  doc.text("Contact:", 14, y);
  doc.text(poData.supplier?.person || "", 40, y);
  incrementY(10);

  // Notes
  doc.setFont(undefined, "bold");
  doc.text("Additional Notes:", 14, y);
  doc.setFont(undefined, "normal");
  incrementY(6);
  wrapText(poData.notes || "none", 18, 180);
  incrementY(10);

  // Terms
  doc.setFont(undefined, "bold");
  const tcTitle = "Terms and Conditions:";
  doc.text(tcTitle, 14, y);
  doc.line(14, y + 1, 14 + doc.getTextWidth(tcTitle) + 1, y + 1);
  doc.setFont(undefined, "normal");
  incrementY(6);
  const terms = poData.terms || [];
  terms.forEach((term, index) => {
    wrapText(`${index + 1}. ${term}`, 18, 180);
  });

  doc.text(`${terms.length + 1}. Payment Terms:`, 18, y);
  doc.text(poData.paymentTC || "", 52, y);
  return doc;
};